import { create, type StoreApi, type UseBoundStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface StoredAuthUser {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly nameEn: string | null;
  readonly nameAr: string | null;
  readonly userType: "staff" | "customer";
  // null for the tenant's own staff (ADMIN/EMPLOYEE) — only a customer has one.
  // The names are optional: sessions stored before they were kept lack them.
  readonly account: {
    readonly id: string;
    readonly nameEn?: string | undefined;
    readonly nameAr?: string | undefined;
  } | null;
  readonly isAccountAdmin: boolean;
  readonly permissions: readonly string[];
  /** The user's own profile picture; absent means the default avatar. */
  readonly avatarUrl?: string | null;
}

/** The fields a user can change on their own profile. */
export type StoredAuthUserUpdate = Partial<
  Pick<StoredAuthUser, "nameEn" | "nameAr" | "avatarUrl">
>;

export type LogoutReason = "manual" | "timeout" | "expired" | null;

export interface AuthState {
  readonly accessToken: string | null;
  readonly user: StoredAuthUser | null;
  readonly pendingOtpUsername: string | null;
  readonly initialized: boolean;
  readonly logoutReason: LogoutReason;
  /**
   * Forgot/reset-password's own in-progress state -- deliberately separate
   * from pendingOtpUsername (login's post-credentials OTP): different
   * backend flow entirely (see verify-reset-otp), just parked here since
   * this is the app's one established place for "auth flow in progress"
   * state that must survive a route change.
   */
  readonly pendingPasswordResetEmail: string | null;
  /** Single-use, returned by verify-reset-otp; consumed by PATCH /auth/reset-password. */
  readonly passwordResetToken: string | null;
  readonly passwordResetTokenExpiresAt: string | null;
  setSession(accessToken: string, user: StoredAuthUser): void;
  /** Patches the signed-in user (e.g. after a profile edit); no-op signed out. */
  updateUser(update: StoredAuthUserUpdate): void;
  beginOtp(username: string): void;
  beginPasswordReset(email: string): void;
  setPasswordResetToken(token: string, expiresAt: string): void;
  clearPasswordReset(): void;
  setInitialized(initialized: boolean): void;
  clearSession(reason?: LogoutReason): void;
}

export interface AuthStoreOptions {
  /** Development-only escape hatch. Production tokens remain memory-only. */
  readonly persistSession?: boolean;
}

const initialState = {
  accessToken: null,
  user: null,
  pendingOtpUsername: null,
  initialized: false,
  logoutReason: null,
  pendingPasswordResetEmail: null,
  passwordResetToken: null,
  passwordResetTokenExpiresAt: null,
} as const;

function stateCreator(set: StoreApi<AuthState>["setState"]): AuthState {
  return {
    ...initialState,
    setSession: (accessToken, user) =>
      set({ accessToken, user, pendingOtpUsername: null, logoutReason: null }),
    updateUser: (update) =>
      set((state) =>
        state.user === null ? {} : { user: { ...state.user, ...update } },
      ),
    beginOtp: (pendingOtpUsername) => set({ pendingOtpUsername }),
    beginPasswordReset: (pendingPasswordResetEmail) =>
      set({
        pendingPasswordResetEmail,
        passwordResetToken: null,
        passwordResetTokenExpiresAt: null,
      }),
    setPasswordResetToken: (passwordResetToken, passwordResetTokenExpiresAt) =>
      set({ passwordResetToken, passwordResetTokenExpiresAt }),
    clearPasswordReset: () =>
      set({
        pendingPasswordResetEmail: null,
        passwordResetToken: null,
        passwordResetTokenExpiresAt: null,
      }),
    setInitialized: (initialized) => set({ initialized }),
    clearSession: (logoutReason = "manual") =>
      set({ ...initialState, initialized: true, logoutReason }),
  };
}

export function createAuthStore(
  options: AuthStoreOptions = {},
): UseBoundStore<StoreApi<AuthState>> {
  if (!options.persistSession) return create<AuthState>()(stateCreator);

  return create<AuthState>()(
    persist(stateCreator, {
      name: "nasam-dev-auth-session",
      storage: createJSONStorage(() => localStorage),
      partialize: ({ accessToken, user }) => ({ accessToken, user }),
    }),
  );
}

export const useAuthStore = createAuthStore();
